// Copyright (c) Ping. All rights reserved.
//
// Business Directory + KYC/KYB API adapter.
//
// Directory:
//   GET  /v1/directory/categories
//   GET  /v1/directory/companies          (public, paginated + filtered)
//   POST /v1/directory/companies          (create)
//   GET  /v1/directory/companies/mine     (owner's companies)
//   GET  /v1/directory/companies/{id}
//   PUT  /v1/directory/companies/{id}     (owner only)
//   DELETE /v1/directory/companies/{id}  (owner only)
//   POST /v1/directory/companies/{id}/verification  (submit KYB docs)
//   POST /v1/directory/companies/{id}/kyb           (start Persona KYB)
//
// Personal KYC:
//   GET  /v1/me/kyc
//   POST /v1/me/kyc

import { del, get, post, put } from '@core/network/api';

// ─── Domain types ─────────────────────────────────────────────────────────────

export type EmployeeRange =
  | '1-10'
  | '11-50'
  | '51-200'
  | '201-500'
  | '501-1000'
  | '1000+';

export type VerificationStatus = 'pending' | 'approved' | 'rejected';

export type KycStatus = 'none' | 'pending' | 'needs_review' | 'approved' | 'declined';

export interface Category {
  readonly name: string;
  readonly slug: string;
}

export interface CompanyVerification {
  readonly status: VerificationStatus;
  readonly businessRegistrationNumber: string;
  readonly submittedDocuments: readonly string[];
  readonly submittedAt: string;
  readonly verifiedAt: string | null;
}

export interface Company {
  readonly id: string;
  readonly ownerAccountId: string;
  readonly name: string;
  readonly description: string;
  readonly website: string | null;
  readonly logoUrl: string | null;
  readonly locationCity: string | null;
  readonly locationCountry: string | null;
  readonly employeeRange: EmployeeRange | null;
  readonly sector: string | null;
  readonly foundedYear: number | null;
  readonly categories: readonly Category[];
  readonly verified: boolean;
  readonly verification: CompanyVerification | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CompanyListFilters {
  readonly search?: string;
  readonly locationCity?: string;
  readonly locationCountry?: string;
  readonly employeeRange?: EmployeeRange;
  readonly categories?: readonly string[];
  readonly page?: number;
  readonly limit?: number;
}

export interface CompanyListResponse {
  readonly companies: readonly Company[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

export interface CreateCompanyInput {
  readonly name: string;
  readonly description: string;
  readonly categorySlugs: readonly string[];
  readonly website?: string;
  /** External http(s) URL or a `media:<id>` reference to an uploaded logo. */
  readonly logoUrl?: string;
  readonly locationCity?: string;
  readonly locationCountry?: string;
  readonly employeeRange?: EmployeeRange;
  readonly sector?: string;
  readonly foundedYear?: number;
}

export interface UpdateCompanyInput {
  readonly name?: string;
  readonly description?: string;
  readonly categorySlugs?: readonly string[];
  readonly website?: string | null;
  readonly locationCity?: string | null;
  readonly locationCountry?: string | null;
  readonly employeeRange?: EmployeeRange | null;
  readonly sector?: string | null;
  readonly foundedYear?: number | null;
}

export interface SubmitKybInput {
  readonly businessRegistrationNumber: string;
  readonly submittedDocuments: readonly string[];
}

export interface KycStatusResponse {
  readonly status: KycStatus;
}

export interface VerificationStartResponse {
  readonly inquiryId: string;
  readonly hostedUrl: string;
  readonly status: string;
}

// ─── Wire types (server JSON shape) ───────────────────────────────────────────

interface CategoryWire {
  name: string;
  slug: string;
}

interface CategoriesResponseWire {
  categories: CategoryWire[];
}

interface CompanyVerificationWire {
  status: string;
  business_registration_number: string;
  submitted_documents: string[];
  submitted_at: string;
  verified_at?: string | null;
}

interface CompanyWire {
  id: string;
  owner_account_id: string;
  name: string;
  description: string;
  website?: string | null;
  logo_url?: string | null;
  location_city?: string | null;
  location_country?: string | null;
  employee_range?: string | null;
  sector?: string | null;
  founded_year?: number | null;
  categories: CategoryWire[];
  verified: boolean;
  verification?: CompanyVerificationWire | null;
  created_at: string;
  updated_at: string;
}

// Public listing wire shape — the API returns `{ items, meta }`
// (services/api/internal/directory/types.go → ListResponse), NOT
// `{ companies, total, ... }`.
interface CompanyListResponseWire {
  items: CompanyWire[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface KycStatusWire {
  status: string;
}

interface VerificationStartWire {
  inquiry_id: string;
  // The API returns the Persona hosted one-time link as `url`
  // (services/api/internal/verification/types.go → `json:"url"`), NOT
  // `hosted_url`. Reading the wrong key left `hostedUrl` undefined, so the
  // verification popup never opened.
  url: string;
  status: string;
}

// ─── Adapters ─────────────────────────────────────────────────────────────────

function toCategory(w: CategoryWire): Category {
  return { name: w.name, slug: w.slug };
}

function toCompany(w: CompanyWire): Company {
  return {
    id: w.id,
    ownerAccountId: w.owner_account_id,
    name: w.name,
    description: w.description,
    website: w.website ?? null,
    logoUrl: w.logo_url ?? null,
    locationCity: w.location_city ?? null,
    locationCountry: w.location_country ?? null,
    employeeRange: (w.employee_range as EmployeeRange) ?? null,
    sector: w.sector ?? null,
    foundedYear: w.founded_year ?? null,
    categories: (w.categories ?? []).map(toCategory),
    verified: w.verified === true,
    verification: w.verification
      ? {
          status: w.verification.status as VerificationStatus,
          businessRegistrationNumber: w.verification.business_registration_number,
          submittedDocuments: w.verification.submitted_documents ?? [],
          submittedAt: w.verification.submitted_at,
          verifiedAt: w.verification.verified_at ?? null,
        }
      : null,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
  };
}

// ─── Directory endpoints ───────────────────────────────────────────────────────

/**
 * Resolve a company logo reference to a directly-loadable image URL. An
 * uploaded logo is stored as `media:<id>` and resolved to a fresh presigned URL
 * via `GET /v1/media/{id}`; an external http(s) logo passes through unchanged.
 * Returns `undefined` on any miss so the caller falls back to the initials tile.
 * (Kept here — not in core/network/users — so it doesn't pull the `@ping/ui`
 * barrel into the directory view's test bundle.)
 */
export async function resolveCompanyLogoUrl(ref: string): Promise<string | undefined> {
  const trimmed = ref.trim();
  if (trimmed.length === 0) return undefined;
  if (!trimmed.startsWith('media:')) return trimmed;
  const id = trimmed.slice('media:'.length);
  try {
    const r = await get<{ download_url: string }>(
      `/v1/media/${encodeURIComponent(id)}`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
    return r.download_url.length > 0 ? r.download_url : undefined;
  } catch {
    return undefined;
  }
}

export async function getCategories(): Promise<readonly Category[]> {
  const w = await get<CategoriesResponseWire>('/v1/directory/categories');
  return (w.categories ?? []).map(toCategory);
}

export async function getCompanies(
  filters: CompanyListFilters = {},
): Promise<CompanyListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.locationCity) params.set('location_city', filters.locationCity);
  if (filters.locationCountry) params.set('location_country', filters.locationCountry);
  if (filters.employeeRange) params.set('employee_range', filters.employeeRange);
  if (filters.categories?.length) {
    // The backend reads repeated `categories` params (OpenAPI: style=form,
    // explode=true → `?categories=a&categories=b`) via `q["categories"]`. A
    // `categories[]` suffix means the server never sees the filter, so
    // category filtering silently did nothing.
    for (const c of filters.categories) params.append('categories', c);
  }
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.limit != null) params.set('limit', String(filters.limit));

  const qs = params.toString();
  const w = await get<CompanyListResponseWire>(
    `/v1/directory/companies${qs ? `?${qs}` : ''}`,
  );
  return {
    companies: (w.items ?? []).map(toCompany),
    total: w.meta?.total ?? 0,
    page: w.meta?.page ?? 1,
    limit: w.meta?.limit ?? 20,
  };
}

export async function getMyCompanies(): Promise<readonly Company[]> {
  // `/mine` returns `{ companies: [...] }` (CompaniesResponse), not a bare
  // array — parsing it as an array previously yielded `[]`, which made the
  // offline sync treat the optimistic row as stale and delete it.
  const w = await get<{ companies?: CompanyWire[] }>('/v1/directory/companies/mine');
  return (w.companies ?? []).map(toCompany);
}

export async function getCompany(id: string): Promise<Company> {
  const w = await get<CompanyWire>(`/v1/directory/companies/${id}`);
  return toCompany(w);
}

export async function createCompany(input: CreateCompanyInput): Promise<Company> {
  const body: Record<string, unknown> = {
    name: input.name,
    description: input.description,
    category_slugs: input.categorySlugs,
  };
  if (input.website) body.website = input.website;
  if (input.logoUrl) body.logo_url = input.logoUrl;
  if (input.locationCity) body.location_city = input.locationCity;
  if (input.locationCountry) body.location_country = input.locationCountry;
  if (input.employeeRange) body.employee_range = input.employeeRange;
  if (input.sector) body.sector = input.sector;
  if (input.foundedYear != null) body.founded_year = input.foundedYear;

  const w = await post<CompanyWire>('/v1/directory/companies', body);
  return toCompany(w);
}

export async function updateCompany(
  id: string,
  input: UpdateCompanyInput,
): Promise<Company> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.categorySlugs !== undefined) body.category_slugs = input.categorySlugs;
  if (input.website !== undefined) body.website = input.website;
  if (input.locationCity !== undefined) body.location_city = input.locationCity;
  if (input.locationCountry !== undefined) body.location_country = input.locationCountry;
  if (input.employeeRange !== undefined) body.employee_range = input.employeeRange;
  if (input.sector !== undefined) body.sector = input.sector;
  if (input.foundedYear !== undefined) body.founded_year = input.foundedYear;

  const w = await put<CompanyWire>(`/v1/directory/companies/${id}`, body);
  return toCompany(w);
}

export async function deleteCompany(id: string): Promise<void> {
  await del<void>(`/v1/directory/companies/${id}`);
}

export async function submitKyb(
  companyId: string,
  input: SubmitKybInput,
): Promise<Company> {
  const w = await post<CompanyWire>(
    `/v1/directory/companies/${companyId}/verification`,
    {
      business_registration_number: input.businessRegistrationNumber,
      submitted_documents: input.submittedDocuments,
    },
  );
  return toCompany(w);
}

export async function startKyb(
  companyId: string,
): Promise<VerificationStartResponse> {
  const w = await post<VerificationStartWire>(
    `/v1/directory/companies/${companyId}/kyb`,
    {},
  );
  return { inquiryId: w.inquiry_id, hostedUrl: w.url, status: w.status };
}

// ─── Personal KYC ─────────────────────────────────────────────────────────────

export async function getKycStatus(): Promise<KycStatusResponse> {
  const w = await get<KycStatusWire>('/v1/me/kyc');
  return { status: w.status as KycStatus };
}

export async function startKyc(): Promise<VerificationStartResponse> {
  const w = await post<VerificationStartWire>('/v1/me/kyc', {});
  return { inquiryId: w.inquiry_id, hostedUrl: w.url, status: w.status };
}
